import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { SupabaseDataService } from '@/lib/supabaseDb';

interface AppState {
  painLogs: any[];
  prescribedExercises: any[];
  exerciseCompletions: any[];
  meals: any[];
  documents: any[];
  appointments: any[];
  medications: any[];
  supplements: any[];
  supplementLogs: any[];
  userNotes: any[];
  settings: Record<string, string>;
  loading: boolean;
}

interface AppContextType extends AppState {
  onboardingComplete: boolean;
  refreshData: () => Promise<void>;
  addPainLog: (level: number, location: string, notes: string, timestamp?: Date) => Promise<void>;
  deletePainLog: (id: string) => Promise<void>;
  addExercise: (name: string, sets: number, reps: number, duration: number, scheduleDays: number[], exerciseType?: 'cardio' | 'strength', notes?: string, weight?: number) => Promise<void>;
  updateExercise: (id: string, name: string, sets: number, reps: number, duration: number, scheduleDays: number[], exerciseType: 'cardio' | 'strength', notes: string, weight: number) => Promise<void>;
  deleteExercise: (id: string) => Promise<void>;
  toggleExerciseCompletion: (exerciseId: string, date: string, notes?: string) => Promise<void>;
  updateExerciseCompletionNotes: (exerciseId: string, date: string, notes: string) => Promise<void>;
  addMeal: (meal: { name: string; mealType: string; protein: number; carbs: number; fats: number; calories: number }) => Promise<void>;
  updateMeal: (id: string, meal: { name: string; mealType: string; protein: number; carbs: number; fats: number; calories: number }) => Promise<void>;
  deleteMeal: (id: string) => Promise<void>;
  addAppointment: (title: string, date: string, location: string, notes: string) => Promise<void>;
  deleteAppointment: (id: string) => Promise<void>;
  addDocument: (doc: { name: string; type: string; uri: string; notes: string }) => Promise<void>;
  deleteDocument: (id: string) => Promise<void>;
  addSupplement: (name: string, dosage: string, icon: string, color: string) => Promise<void>;
  deleteSupplement: (id: string) => Promise<void>;
  toggleSupplementLog: (supplementId: string, date: string) => Promise<void>;
  addNote: (note: { title: string; content: string; category: string }) => Promise<void>;
  updateNote: (id: string, note: { title: string; content: string; category: string }) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  setSetting: (key: string, value: string) => Promise<void>;
}

const AppContext = createContext<AppContextType | null>(null);

function parseScheduleDays(s: string | null): number[] {
  if (!s) return [];
  return s.split(',').map(Number).filter(n => !isNaN(n));
}

export function AppProvider({ userId, isGuest = false, children }: { userId: string; isGuest?: boolean; children: React.ReactNode }) {
  const [state, setState] = useState<AppState>({
    painLogs: [],
    prescribedExercises: [],
    exerciseCompletions: [],
    meals: [],
    documents: [],
    appointments: [],
    medications: [],
    supplements: [],
    supplementLogs: [],
    userNotes: [],
    settings: {},
    loading: !isGuest,
  });

  const refreshData = useCallback(async () => {
    if (isGuest) return;
    try {
      const data = await SupabaseDataService.loadAllData(userId);
      const settingsMap: Record<string, string> = {};
      for (const s of data.settings) {
        settingsMap[s.key] = s.value;
      }
      setState({
        painLogs: data.painLogs.map((p: any) => ({ ...p, timestamp: new Date(p.timestamp) })),
        prescribedExercises: data.prescribedExercises.map((e: any) => ({
          ...e,
          scheduleDays: parseScheduleDays(e.schedule_days),
          exerciseType: e.exercise_type || 'strength',
          notes: e.notes || '',
          weight: e.weight || 0,
        })),
        exerciseCompletions: data.exerciseCompletions.map((c: any) => ({
          exerciseId: c.exercise_id,
          date: c.date,
          notes: c.notes || '',
        })),
        meals: data.meals.map((m: any) => ({
          ...m,
          mealType: m.meal_type,
          timestamp: new Date(m.timestamp),
        })),
        documents: data.documents.map((d: any) => ({ ...d, timestamp: new Date(d.timestamp) })),
        appointments: data.appointments.map((a: any) => ({ ...a, date: new Date(a.date) })),
        medications: data.medications,
        supplements: data.supplements,
        supplementLogs: data.supplementLogs.map((l: any) => ({
          date: l.date,
          supplementId: l.supplement_id,
          taken: l.taken,
        })),
        userNotes: (data.userNotes || []).map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp),
        })),
        settings: settingsMap,
        loading: false,
      });
    } catch (err) {
      console.error('Failed to load data:', err);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [userId, isGuest]);

  useEffect(() => {
    refreshData();
  }, [refreshData]);

  const genId = () => Math.random().toString(36).substr(2, 9);

  const addPainLog = async (level: number, location: string, notes: string, timestamp?: Date) => {
    const id = genId();
    const ts = timestamp ?? new Date();
    if (isGuest) {
      setState(prev => ({ ...prev, painLogs: [{ id, level, location, notes, timestamp: ts }, ...prev.painLogs] }));
    } else {
      await SupabaseDataService.insertPainLog(userId, { id, level, location, notes, timestamp: ts.toISOString() });
      await refreshData();
    }
  };

  const deletePainLog = async (id: string) => {
    if (isGuest) {
      setState(prev => ({ ...prev, painLogs: prev.painLogs.filter(p => p.id !== id) }));
    } else {
      await SupabaseDataService.deletePainLog(id);
      await refreshData();
    }
  };

  const addExercise = async (name: string, sets: number, reps: number, duration: number, scheduleDays: number[], exerciseType: 'cardio' | 'strength' = 'strength', notes: string = '', weight: number = 0) => {
    const id = genId();
    if (isGuest) {
      setState(prev => ({ ...prev, prescribedExercises: [...prev.prescribedExercises, { id, name, sets, reps, duration, scheduleDays, exerciseType, notes, weight }] }));
    } else {
      await SupabaseDataService.insertPrescribedExercise(userId, { id, name, sets, reps, duration, schedule_days: scheduleDays.join(','), exercise_type: exerciseType, notes, weight });
      await refreshData();
    }
  };

  const updateExercise = async (id: string, name: string, sets: number, reps: number, duration: number, scheduleDays: number[], exerciseType: 'cardio' | 'strength', notes: string, weight: number) => {
    const ex = { id, name, sets, reps, duration, schedule_days: scheduleDays.join(','), exercise_type: exerciseType, notes, weight };
    setState(prev => ({ ...prev, prescribedExercises: prev.prescribedExercises.map(e => e.id === id ? { ...e, name, sets, reps, duration, scheduleDays, exerciseType, notes, weight } : e) }));
    if (!isGuest) await SupabaseDataService.updatePrescribedExercise(ex);
  };

  const deleteExercise = async (id: string) => {
    if (isGuest) {
      setState(prev => ({ ...prev, prescribedExercises: prev.prescribedExercises.filter(e => e.id !== id), exerciseCompletions: prev.exerciseCompletions.filter(c => c.exerciseId !== id) }));
    } else {
      await SupabaseDataService.deletePrescribedExercise(id);
      await refreshData();
    }
  };

  const toggleExerciseCompletion = async (exerciseId: string, date: string, notes?: string) => {
    if (isGuest) {
      setState(prev => {
        const exists = prev.exerciseCompletions.find(c => c.exerciseId === exerciseId && c.date === date);
        if (exists) {
          return { ...prev, exerciseCompletions: prev.exerciseCompletions.filter(c => !(c.exerciseId === exerciseId && c.date === date)) };
        }
        return { ...prev, exerciseCompletions: [...prev.exerciseCompletions, { exerciseId, date, notes: notes || '' }] };
      });
    } else {
      await SupabaseDataService.toggleExerciseCompletion(userId, exerciseId, date, notes);
      await refreshData();
    }
  };

  const updateExerciseCompletionNotes = async (exerciseId: string, date: string, notes: string) => {
    if (isGuest) {
      setState(prev => ({ ...prev, exerciseCompletions: prev.exerciseCompletions.map(c => c.exerciseId === exerciseId && c.date === date ? { ...c, notes } : c) }));
    } else {
      await SupabaseDataService.updateExerciseCompletionNotes(exerciseId, date, notes);
      await refreshData();
    }
  };

  const addMeal = async (meal: { name: string; mealType: string; protein: number; carbs: number; fats: number; calories: number }) => {
    const id = genId();
    const timestamp = new Date();
    if (isGuest) {
      setState(prev => ({ ...prev, meals: [{ id, ...meal, timestamp }, ...prev.meals] }));
    } else {
      await SupabaseDataService.insertMeal(userId, { id, name: meal.name, meal_type: meal.mealType, protein: meal.protein, carbs: meal.carbs, fats: meal.fats, calories: meal.calories, timestamp: timestamp.toISOString() });
      await refreshData();
    }
  };

  const updateMeal = async (id: string, meal: { name: string; mealType: string; protein: number; carbs: number; fats: number; calories: number }) => {
    if (isGuest) {
      setState(prev => ({ ...prev, meals: prev.meals.map(m => m.id === id ? { ...m, ...meal } : m) }));
    } else {
      const timestamp = new Date().toISOString();
      await SupabaseDataService.updateMeal({ id, name: meal.name, meal_type: meal.mealType, protein: meal.protein, carbs: meal.carbs, fats: meal.fats, calories: meal.calories, timestamp });
      await refreshData();
    }
  };

  const deleteMeal = async (id: string) => {
    if (isGuest) {
      setState(prev => ({ ...prev, meals: prev.meals.filter(m => m.id !== id) }));
    } else {
      await SupabaseDataService.deleteMeal(id);
      await refreshData();
    }
  };

  const addDocument = async (doc: { name: string; type: string; uri: string; notes: string }) => {
    const id = genId();
    const timestamp = new Date();
    if (isGuest) {
      setState(prev => ({ ...prev, documents: [{ id, ...doc, timestamp }, ...prev.documents] }));
    } else {
      await SupabaseDataService.insertDocument(userId, { id, name: doc.name, type: doc.type, uri: doc.uri, notes: doc.notes, timestamp: timestamp.toISOString() });
      await refreshData();
    }
  };

  const deleteDocument = async (id: string) => {
    if (isGuest) {
      setState(prev => ({ ...prev, documents: prev.documents.filter((d: any) => d.id !== id) }));
    } else {
      await SupabaseDataService.deleteDocument(id);
      await refreshData();
    }
  };

  const addAppointment = async (title: string, date: string, location: string, notes: string) => {
    const id = genId();
    if (isGuest) {
      setState(prev => ({ ...prev, appointments: [...prev.appointments, { id, title, date: new Date(date), location, notes }] }));
    } else {
      await SupabaseDataService.insertAppointment(userId, { id, title, date, location, notes });
      await refreshData();
    }
  };

  const deleteAppointment = async (id: string) => {
    if (isGuest) {
      setState(prev => ({ ...prev, appointments: prev.appointments.filter(a => a.id !== id) }));
    } else {
      await SupabaseDataService.deleteAppointment(id);
      await refreshData();
    }
  };

  const addSupplement = async (name: string, dosage: string, icon: string, color: string) => {
    const id = genId();
    if (isGuest) {
      setState(prev => ({ ...prev, supplements: [...prev.supplements, { id, name, dosage, icon, color }] }));
    } else {
      await SupabaseDataService.insertSupplement(userId, { id, name, dosage, icon, color });
      await refreshData();
    }
  };

  const deleteSupplement = async (id: string) => {
    if (isGuest) {
      setState(prev => ({ ...prev, supplements: prev.supplements.filter(s => s.id !== id) }));
    } else {
      await SupabaseDataService.deleteSupplement(id);
      await refreshData();
    }
  };

  const toggleSupplementLog = async (supplementId: string, date: string) => {
    if (isGuest) {
      setState(prev => {
        const exists = prev.supplementLogs.find(l => l.supplementId === supplementId && l.date === date);
        if (exists) {
          return { ...prev, supplementLogs: prev.supplementLogs.map(l => l.supplementId === supplementId && l.date === date ? { ...l, taken: !l.taken } : l) };
        }
        return { ...prev, supplementLogs: [...prev.supplementLogs, { supplementId, date, taken: true }] };
      });
    } else {
      await SupabaseDataService.toggleSupplementLog(userId, supplementId, date);
      await refreshData();
    }
  };

  const addNote = async (note: { title: string; content: string; category: string }) => {
    const id = genId();
    const timestamp = new Date();
    setState(prev => ({ ...prev, userNotes: [{ id, ...note, timestamp }, ...prev.userNotes] }));
    if (!isGuest) {
      await SupabaseDataService.insertNote(userId, { id, ...note, timestamp: timestamp.toISOString() });
    }
  };

  const updateNote = async (id: string, note: { title: string; content: string; category: string }) => {
    const timestamp = new Date();
    setState(prev => ({ ...prev, userNotes: prev.userNotes.map(n => n.id === id ? { ...n, ...note, timestamp } : n) }));
    if (!isGuest) {
      await SupabaseDataService.updateNote({ id, ...note, timestamp: timestamp.toISOString() });
    }
  };

  const deleteNote = async (id: string) => {
    setState(prev => ({ ...prev, userNotes: prev.userNotes.filter(n => n.id !== id) }));
    if (!isGuest) {
      await SupabaseDataService.deleteNote(id);
    }
  };

  const setSetting = async (key: string, value: string) => {
    if (isGuest) {
      setState(prev => ({ ...prev, settings: { ...prev.settings, [key]: value } }));
    } else {
      await SupabaseDataService.setSetting(userId, key, value);
      await refreshData();
    }
  };

  const onboardingComplete = state.settings.onboardingComplete === 'true';

  return (
    <AppContext.Provider value={{
      ...state,
      onboardingComplete,
      refreshData,
      addPainLog,
      deletePainLog,
      addExercise,
      updateExercise,
      deleteExercise,
      toggleExerciseCompletion,
      updateExerciseCompletionNotes,
      addMeal,
      updateMeal,
      deleteMeal,
      addDocument,
      deleteDocument,
      addAppointment,
      deleteAppointment,
      addSupplement,
      deleteSupplement,
      toggleSupplementLog,
      addNote,
      updateNote,
      deleteNote,
      setSetting,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export function useAppData() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppData must be used within AppProvider');
  return context;
}
